import React, { useEffect, useMemo, useState } from 'react';
import './StagingPromotions.css';

const tabs = [
  'All', 'Active', 'Scheduled',
  'Paused', 'Expired', 'Exhausted'
];

const money = value =>
  '£' + (Number(value) || 0).toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

const date = value => value
  ? new Date(String(value).slice(0, 10) + 'T12:00:00')
      .toLocaleDateString('en-GB')
  : '—';

const csvCell = value =>
  '"' + String(value ?? '').replace(/"/g, '""') + '"';

export default function StagingPromotions() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState('All');
  const [search, setSearch] = useState('');
  const [provider, setProvider] = useState('');
  const [sort, setSort] = useState('newest');
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        const admin = JSON.parse(
          localStorage.getItem('admin') || 'null'
        );

        if (!admin?.accessToken) {
          throw new Error('Please sign in again.');
        }

        const response = await fetch(
          '/api/dashboards/admin/promotions',
          {
            headers: {
              'x-access-token': admin.accessToken
            }
          }
        );

        if (!response.ok) {
          throw new Error('API returned ' + response.status);
        }

        const data = await response.json();

        if (!data.success) {
          throw new Error('Unable to retrieve promotions.');
        }

        if (active) {
          setRows(data.promotions || []);
          setError('');
          setLoading(false);
        }
      } catch (e) {
        if (active) {
          setError(e.message);
          setLoading(false);
        }
      }
    }

    load();

    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, 30000);

    return () => {
      active = false;
      clearInterval(timer);
    };
  }, []);

  const providers = useMemo(
    () => [...new Set(rows.map(r => r.provider))].sort(),
    [rows]
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase();

    return rows.filter(r =>
      (tab === 'All' || r.status === tab) &&
      (!provider || r.provider === provider) &&
      (!q || [r.code, r.provider, r.description].some(
        value => String(value || '').toLowerCase().includes(q)
      ))
    ).sort((a, b) => {
      if (sort === 'code') {
        return String(a.code).localeCompare(String(b.code));
      }
      if (sort === 'oldest') return a.id - b.id;
      if (sort === 'redemptions') {
        return b.confirmedRedemptions - a.confirmedRedemptions;
      }
      return b.id - a.id;
    });
  }, [rows, tab, search, provider, sort]);

  function exportCsv() {
    const headers = [
      'Code', 'Provider', 'Discount', 'Status',
      'Start date', 'End date', 'Redemptions',
      'Confirmed redemptions', 'Completed appointments',
      'Confirmed discounts', 'Completed discounts'
    ];

    const data = filtered.map(r => [
      r.code,
      r.provider,
      r.discountType === 'percentage'
        ? r.discount + '%'
        : money(r.discount),
      r.status,
      r.startDate,
      r.endDate,
      r.redemptions,
      r.confirmedRedemptions,
      r.completedAppointments,
      r.confirmedDiscounts,
      r.completedDiscounts
    ]);

    const content = [headers, ...data]
      .map(row => row.map(csvCell).join(','))
      .join('\r\n');

    const blob = new Blob(['\uFEFF' + content], {
      type: 'text/csv;charset=utf-8'
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'beautyspot-promotions.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <main className="bspromo">
      <header className="bspromo-header">
        <div>
          <small>BEAUTY SPOT / PROMOTIONS</small>
          <h1>Promotions</h1>
          <p>Live promo codes and redemption performance · Read only</p>
        </div>
        <button onClick={exportCsv}>Export CSV</button>
      </header>

      <div className="bspromo-kpis">
        <div>
          <small>Promo codes</small>
          <strong>{rows.length}</strong>
        </div>
        <div>
          <small>Confirmed redemptions</small>
          <strong>
            {rows.reduce((n, r) => n + r.confirmedRedemptions, 0)}
          </strong>
        </div>
        <div>
          <small>Completed appointments</small>
          <strong>
            {rows.reduce((n, r) => n + r.completedAppointments, 0)}
          </strong>
        </div>
        <div>
          <small>Completed discounts</small>
          <strong>
            {money(rows.reduce(
              (n, r) => n + r.completedDiscounts, 0
            ))}
          </strong>
        </div>
      </div>

      <section className="bspromo-panel">
        <nav className="bspromo-tabs">
          {tabs.map(t => (
            <button
              key={t}
              className={tab === t ? 'selected' : ''}
              onClick={() => setTab(t)}
            >
              {t} ({rows.filter(
                r => t === 'All' || r.status === t
              ).length})
            </button>
          ))}
        </nav>

        <div className="bspromo-filters">
          <label>
            Search promo codes
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Code or provider"
            />
          </label>

          <label>
            Service provider
            <select
              value={provider}
              onChange={e => setProvider(e.target.value)}
            >
              <option value="">All providers</option>
              {providers.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </label>

          <label>
            Sort by
            <select
              value={sort}
              onChange={e => setSort(e.target.value)}
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="code">Code A–Z</option>
              <option value="redemptions">Most redemptions</option>
            </select>
          </label>
        </div>

        {error && <p className="bspromo-error">{error}</p>}

        {loading ? (
          <p>Loading promotions…</p>
        ) : (
          <div className="bspromo-scroll">
            <table>
              <thead>
                <tr>
                  <th>Promo code</th>
                  <th>Service provider</th>
                  <th>Discount</th>
                  <th>Valid dates</th>
                  <th>Redemptions</th>
                  <th>Completed discounts</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id}>
                    <td><strong>{r.code}</strong></td>
                    <td>{r.provider}</td>
                    <td>
                      {r.discountType === 'percentage'
                        ? r.discount + '%'
                        : money(r.discount)}
                    </td>
                    <td>
                      {date(r.startDate)} – {date(r.endDate)}
                    </td>
                    <td>{r.confirmedRedemptions}</td>
                    <td>{money(r.completedDiscounts)}</td>
                    <td>{r.status}</td>
                    <td>
                      <button onClick={() => setSelected(r)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {!filtered.length && (
              <p>No promotions match these filters.</p>
            )}
          </div>
        )}
      </section>


      {selected && (
        <div
          className="bspromo-overlay"
          onClick={() => setSelected(null)}
        >
          <aside
            className="bspromo-drawer"
            onClick={e => e.stopPropagation()}
          >
            <div className="bspromo-drawer-top">
              <span>BEAUTY SPOT ADMIN</span>
              <button
                className="bspromo-close"
                aria-label="Close promotion details"
                onClick={() => setSelected(null)}
              >
                ×
              </button>
            </div>

            <h2 className="bspromo-code-title">
              {selected.code}
            </h2>

            <button
              className="bspromo-back"
              onClick={() => setSelected(null)}
            >
              ← Back
            </button>

            <div className="bspromo-drawer-summary">
              <div>
                <strong className="bspromo-discount-title">
                  {selected.discountType === 'percentage'
                    ? selected.discount + '% off'
                    : money(selected.discount) + ' off'}
                </strong>
                <small>{selected.provider}</small>
              </div>
              <span className={
                'bspromo-status bspromo-status-' +
                String(selected.status).toLowerCase()
              }>
                {selected.status}
              </span>
            </div>

            <div className="bspromo-terms-grid">
              {[
                ['Valid from', date(selected.startDate)],
                ['Valid to', date(selected.endDate)],
                ['Description', selected.description || '—'],
                ['Eligibility', selected.categoryNames
                  ? 'Selected categories'
                  : 'Not specified'],
                ['Total usage limit',
                  selected.usageLimit == null
                    ? 'Unlimited'
                    : selected.usageLimit + ' redemptions'],
                ['Recorded uses', selected.redemptions]
              ].map(([label, value]) => (
                <div key={label}>
                  <small>{label}</small>
                  <strong>{value}</strong>
                </div>
              ))}
            </div>

            <div className="bspromo-performance">
              <div>
                <small>Confirmed redemptions</small>
                <strong>
                  {selected.confirmedRedemptions}
                  {selected.usageLimit != null
                    ? ' / ' + selected.usageLimit
                    : ''}
                </strong>
              </div>

              <div>
                <small>Completed appointments</small>
                <strong>{selected.completedAppointments}</strong>
              </div>

              <div>
                <small>Discounts on confirmed bookings</small>
                <strong>{money(selected.confirmedDiscounts)}</strong>
              </div>

              <div>
                <small>Discounts on completed appointments</small>
                <strong>{money(selected.completedDiscounts)}</strong>
              </div>
            </div>

            <h3 className="bspromo-linked-heading">
              Linked bookings
            </h3>

            <div className="bspromo-linked-table">
              <table>
                <thead>
                  <tr>
                    <th>Booking</th>
                    <th>Status</th>
                    <th>Discount</th>
                  </tr>
                </thead>
                <tbody>
                  {selected.bookings.length ? (
                    selected.bookings.map(b => (
                      <tr key={b.id}>
                        <td>
                          <strong>
                            {b.reference || '#' + b.id}
                          </strong>
                          <small>{date(b.date)}</small>
                        </td>
                        <td>{b.status}</td>
                        <td>{money(b.discount)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="bspromo-empty">
                        No linked bookings.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </aside>
        </div>
      )}
    </main>
  );
}
